// Errors, Analytics, and Metrics shape is loosely based on @sdk/observability
// frontend/sail-next/packages/sdk/src/observability/types.tsx
// Things like project or various other params should be handled by the caller,
// passing wrapped interfaces to the client.

// We should try to move to use @sdk/observability as a dependency directly

import {createContext, ReactNode, useContext} from 'react';

export interface Analytics {
  action: (eventName: string, parameters?: Record<string, unknown>) => void;
  track: (eventName: string, parameters?: Record<string, unknown>) => void;
  viewed: (eventName: string, parameters?: Record<string, unknown>) => void;
  requestAnalyticsEventId: () => Promise<string | null>;
}

export type ErrorScope = {
  tags?: Record<string, string>;
  extras?: Record<string, unknown>;
};

export interface Errors {
  reportError: (error: string, scope?: ErrorScope) => string;
  reportWarning: (error: string, scope?: ErrorScope) => void;
  reportInfo: (error: string, scope?: ErrorScope) => void;
}

export type MetricParams = Record<string, string | number | boolean>;
export type MetricTags = Record<string, string | number | boolean>;

export interface Metrics {
  increment: (metric: string, tags?: MetricTags) => void;
  count: (metric: string, value: number, tags?: MetricTags) => void;
  gauge: (
    metric: string,
    measurements: MetricParams,
    tags?: MetricTags,
  ) => void;
  timing: (metric: string, value: number, tags?: MetricTags) => void;
  set: (metric: string, values: MetricParams, tags?: MetricTags) => void;
}

export type ObservabilityConfig = {
  errors?: Partial<Errors>;
  analytics?: Partial<Analytics>;
  metrics?: Partial<Metrics>;
};

export interface Observability {
  errors: Errors;
  analytics: Analytics;
  metrics: Metrics;
}

// provide a full Observability interface with no-op implementations
// for partial config so that clients can call without checking
export const initObservability = (
  config: ObservabilityConfig = {},
): Observability => {
  const noop = (): Promise<string | null> => Promise.resolve(null);
  const noopString = () => '';

  const errors: Errors = {
    reportError: config.errors?.reportError || noopString,
    reportWarning: config.errors?.reportWarning || noop,
    reportInfo: config.errors?.reportInfo || noop,
  };

  const analytics: Analytics = {
    action: config.analytics?.action || noop,
    track: config.analytics?.track || noop,
    viewed: config.analytics?.viewed || noop,
    requestAnalyticsEventId: config.analytics?.requestAnalyticsEventId || noop,
  };

  const metrics: Metrics = {
    increment: config.metrics?.increment || noop,
    count: config.metrics?.count || noop,
    gauge: config.metrics?.gauge || noop,
    timing: config.metrics?.timing || noop,
    set: config.metrics?.set || noop,
  };

  return {
    errors,
    analytics,
    metrics,
  };
};

const ObservabilityContext = createContext<Observability>(initObservability());

type Props = {
  config: ObservabilityConfig;
  children: ReactNode;
};

export function ObservabilityProvider({config, children}: Props) {
  return (
    <ObservabilityContext.Provider value={initObservability(config)}>
      {children}
    </ObservabilityContext.Provider>
  );
}

export function useObservability() {
  return useContext(ObservabilityContext);
}

export function useAnalytics() {
  return useObservability().analytics;
}

export function useMetrics() {
  return useObservability().metrics;
}

export function useErrors() {
  return useObservability().errors;
}
